import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { findDOMNode } from 'react-dom'
import { DraggableCore } from 'react-draggable'
import { withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import {
  compose,
  withHandlers,
  withProps,
  withState,
  getContext,
  lifecycle
} from 'recompose'
import {
  placeBlock as placeBlockAction,
  pasteBlocks,
  embedBlock
} from '../../store/actions/blocks'
import { selectBlocks } from '../../store/actions/selectedBlocks'
import {
  blockGroupsSelector,
  colWidthSelector,
  sectionLayoutAdjustedSelector,
  placementPositionSelector,
  placementSelector,
  dragSnappedSelector,
  boardFullSelector,
  cropBlockIdSelector
} from '../../store/selectors/boardSelectors'
import {
  placementBlockSelector,
  placementModeSelector,
  placementWidthSelector,
  modeSelector
} from '../../store/selectors/selectors'
import Rectangle from '../../utils/Rectangle'
import Board from '../Board/Board'
import {
  updateViewDate,
  setPlacementPosition
} from '../../store/actions/boards'
import { Shortcuts } from 'react-shortcuts'
import { firebaseConnect } from 'react-redux-firebase'
import { uploadFilesToQueue, urlToQueue } from '../../store/actions/upload'
import { Menu } from '../common/Menu'
import { userSelector } from '../../store/selectors/baseSelectors'
import { Selectable } from './Block/SelectableContainer'
import { SectionResize } from './Section/Section'
import { TooltipComponent } from '../common/Tooltip'
import styled from 'react-emotion'
import { rowHeight } from '../../style'
import textToBlock from '../../utils/textToBlock'

/**
 * Track mouse if placing block
 */
// TODO use withsStateHandlers ?
export const trackMouse = compose(
  withState('element', 'storeElement'),
  withHandlers({
    storeRef: props => ref => props.storeElement(findDOMNode(ref)),
    // setting to null messes up DragableCore if you drag out of element
    //mouseLeave: ({ setPlacementPosition }) => () => setPlacementPosition(null),
    mouseMove: props => {
      // don't track if not needed
      const { element, placementBlock, colWidth } = props
      if (
        dragType || // dragging a placement
        element === undefined ||
        !placementBlock
      ) {
        return () => {}
      }

      const { setPlacementPosition, zoom } = props

      return event => {
        const layout = element.getBoundingClientRect()
        setPlacementPosition({
          x: (event.clientX - layout.left) / colWidth / zoom,
          y: (event.clientY - layout.top) / colWidth / zoom
        })
      }
    }
  })
)

/**
 * track dragging out a rectangle
 * either for block placement
 */
const placement = withHandlers({
  placementDragStart: ({ setPlacementPosition, colWidth }) => (event, data) => {
    return setPlacementPosition({
      rectangle: {
        top: (data.lastY - rowHeight.default) / colWidth,
        left: data.lastX / colWidth,
        width: 0,
        height: 0
      }
    })
  },
  placementDrag: ({ placementPosition, setPlacementPosition, colWidth }) => (
    event,
    data
  ) => {
    const rectangle = placementPosition.rectangle
    rectangle.width += data.deltaX / colWidth
    rectangle.height += data.deltaY / colWidth
    setPlacementPosition({
      rectangle: rectangle
    })
  },
  placementDragStop: props => event => {
    const {
      setPlacementPosition,
      sections,
      placement,
      placementBlock: block,
      placeBlock
    } = props

    const { top, left, width, height } = placement.layout
    const section = [...sections.values()]
      .reverse()
      .find(section => section.top <= top)

    const update = {
      top: top - section.top,
      column: left,
      width,
      section: section,
      stackingOrder: new Date().valueOf() - 1494110302577
    }
    if (!block.aspect && !block.text) update.height = height
    placeBlock(block, update)

    setPlacementPosition(null)
  }
})

/**
 * track dragging out a rectangle
 * either for multi-select
 */
const multiSelect = compose(
  withState('selection', 'setSelection', null),
  withHandlers({
    multiselectDragStart: ({ setSelection, colWidth }) => (event, data) => {
      return setSelection(
        new Rectangle({
          top: (data.lastY - rowHeight.default) / colWidth,
          left: data.lastX / colWidth,
          width: 0,
          height: 0
        })
      )
    },
    multiselectDrag: ({ selection, setSelection, colWidth }) => (
      event,
      data
    ) => {
      selection.width += data.deltaX / colWidth
      selection.height += data.deltaY / colWidth
      setSelection(selection)
    },
    multiselectDragStop: props => event => {
      const { setSelection, select, blockGroups } = props
      const selection = props.selection.positive()

      let blocksIn = []
      Object.values(blockGroups).forEach(({ id, layout }) => {
        if (layout.overlaps(selection)) blocksIn.push(id)
      })
      select(blocksIn, event.shiftKey)

      // calling setSelection(null) directly results in no click even propagating to the document
      // that makes useClickOutside sad. TODO figure out WTF
      setTimeout(() => setSelection(null), 0)
    }
  })
)

let dragType = null
// TODO: user withHandlers
const dispatchDragHandlers = withProps(props => ({
  dragStart(event, data) {
    if (props.cropping) return
    // detect type of drag and store
    if (props.placementBlock) {
      dragType = 'placement'
    } else {
      dragType = 'multiselect'
    }

    // handle drag type
    props[`${dragType}DragStart`](event, data)
  },
  drag: (event, data) => {
    if (props.cropping) return
    // don't bother if no movement
    if (data.deltaX === 0 && data.deltaY === 0) return

    props[`${dragType}Drag`](event, data)
  },
  dragStop(event, data) {
    if (props.cropping) return
    props[`${dragType}DragStop`](event, data)
    // reset drag type
    dragType = null
  }
}))

const injectPlacementBlock = withProps(
  ({ placement, placementBlock, blockGroups }) => {
    if (!placement) return {}

    const block = {
      id: 'placementBlock',
      ...placementBlock,
      layout: placement.layout
    }

    return {
      blockGroups: {
        ...blockGroups,
        [block.id]: {
          id: block.id,
          stackingOrder: Infinity,
          blocks: [block],
          layout: placement.layout
        }
      }
    }
  }
)

const preventDefault = event => event.preventDefault()
const handleDataTransfer = compose(
  connect(null, {
    pasteBlocks,
    embedBlock,
    uploadFilesToQueue,
    urlToQueue
  }),
  withState('shiftKey', 'storeShiftKey'),
  withHandlers({
    trackShiftKey: props => event => props.storeShiftKey(event.shiftKey),
    handleDataTransfer: props => async event => {
      if (props.textEditMode) return
      console.groupCollapsed('Data Transfer')

      if (event.dataTransfer) event.preventDefault()

      const dataTransfer = event.clipboardData || event.dataTransfer
      if (dataTransfer.files.length) {
        Array.prototype.forEach.call(dataTransfer.files, file =>
          console.log(
            `%cFile: %c${file.name}(${file.type})`,
            'font-weight: bold; color: #66c',
            'font-weight: normal; color: unset'
          )
        )
        event.preventDefault()
        props.uploadFilesToQueue(dataTransfer.files)
        console.groupEnd()
        return
      }

      const getSrcFromHtml = html => {
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')
        const imgs = doc.getElementsByTagName('img')
        // TODO check srcset for higher res
        if (imgs.length) {
          const src = imgs[0].getAttribute('src')
          console.log(
            `%cFound <img> tag: %c${src}`,
            'font-weight: bold; color: #66c',
            'font-weight: normal; color: unset'
          )
          return src
        }
        // TODO use DOMParser here too
        // .getElementsByTagName('a')[0].style.backgroundImage
        // TODO handle relative urls
        const match = html.match(
          /background(-image)? *:.*?url\((&quot;)?(https?:\/\/.+?)(&quot;)?\)/
        )
        if (match) {
          console.log(
            `%cFound background image: %c${match[3]}`,
            'font-weight: bold; color: #66c',
            'font-weight: normal; color: unset'
          )
          return match[3]
        }
        console.log(
          `%cNo Image Found in text/html`,
          'font-weight: bold; color: #66c'
        )
      }

      const processLink = link => {
        const url = new (window.URL || window.webkitURL)(link)
        // check for google redirect and return target
        if (
          url.host.match(/\.google\./) &&
          url.pathname === '/url' &&
          url.searchParams.has('url')
        ) {
          console.log(url)
          return url.searchParams.get('url')
        }

        // return raw
        return link
      }

      const getData = data => {
        const type = data.type
        return new Promise(resolve => {
          const ensureShitBrowsersResolve = setTimeout(
            () => resolve({ type }),
            600
          )
          data.getAsString(value => {
            clearTimeout(ensureShitBrowsersResolve)
            resolve({ type, value })
          })
        })
      }

      const promises = []
      for (var i = 0; i < dataTransfer.items.length; i += 1) {
        const item = dataTransfer.items[i]
        if (item.kind === 'string')
          promises.push(getData(dataTransfer.items[i]))
        else console.info('Other kind', item.kind)
      }

      Promise.all(promises).then(results => {
        let link, image, embed, gotSomething
        results.forEach(({ type, value }) => {
          console.log(
            `%c${type}: %c${value}`,
            'font-weight: bold; color: #66c',
            'font-weight: normal; color: unset'
          )
          if (value) {
            gotSomething = true
            if (type === 'text/html') {
              image = getSrcFromHtml(value)
            } else if (type === 'text/uri-list') {
              link = processLink(value)
            } else if (type === 'text/plain') {
              embed = textToBlock(value)
            }
          }
        })
        if (image) {
          props.urlToQueue(image, { originalLink: link })
        } else if (embed) {
          console.log(embed)
          props.embedBlock(embed, props.getScrollTop())
        }

        // if we didn't paste somewthing from the clipboard, paste our own blocks
        if (!gotSomething) {
          props.pasteBlocks(props.getScrollTop(), props.shiftKey)
        }
        console.groupEnd()
      })
    }
  }),
  lifecycle({
    componentDidMount() {
      document.addEventListener('paste', this.props.handleDataTransfer, false)
      document.addEventListener('dragover', preventDefault, false)
      document.addEventListener('drop', this.props.handleDataTransfer, false)
      document.addEventListener('keydown', this.props.trackShiftKey)
      document.addEventListener('keyup', this.props.trackShiftKey)
    },
    componentWillUnmount() {
      document.removeEventListener('paste', this.props.handleDataTransfer)
      document.removeEventListener('dragover', preventDefault)
      document.removeEventListener('drop', this.props.handleDataTransfer)
      document.removeEventListener('keydown', this.props.trackShiftKey)
      document.removeEventListener('keyup', this.props.trackShiftKey)
    }
  })
)

let scrollElement
const manageScrollPosition = compose(
  withHandlers({
    storeScrollRef: () => ref => {
      scrollElement = findDOMNode(ref)
    },
    getScrollTop: () => () => scrollElement.scrollTop
  }),
  getContext({ router: PropTypes.object }),
  // withContext({ saveBoardScroll: PropTypes.func }, props => ({
  //   saveBoardScroll: () => {
  //     props.router.history.replace('.', {
  //       boardScroll: scrollElement.scrollTop
  //     })
  //   }
  // })),
  lifecycle({
    componentDidUpdate() {
      const { scrollTo, ...state } =
        this.props.router.history.location.state || {}
      if (scrollTo !== undefined) {
        Object.values(this.props.blockGroups).find(group => {
          const colWidth = this.props.colWidth
          return group.blocks.find(block => {
            if (block.id === scrollTo) {
              let offset =
                (scrollElement.offsetHeight - block.layout.height * colWidth) /
                2
              if (offset < 0) offset = colWidth
              scrollElement.scrollTop = block.layout.top * colWidth - offset
              this.props.router.history.replace('.', state)
              return true
            }
            return false
          })
        })
      }
    }
  })
)

export const enhance = compose(
  connect(
    state => {
      const mode = modeSelector(state)
      const props = {
        colWidth: colWidthSelector(state),
        zoom: 1,
        sections: sectionLayoutAdjustedSelector(state),
        blockGroups: blockGroupsSelector(state),
        mode
      }
      if (mode === 'edit') {
        props.textEditMode = state.board.textEditMode
        props.placementMode = placementModeSelector(state)
        props.placementWidth = placementWidthSelector(state)
        props.placementBlock = placementBlockSelector(state)
        props.placementPosition = placementPositionSelector(state)
        props.placement = placementSelector(state)
        props.cropping = !!cropBlockIdSelector(state)
        props.user = userSelector(state)
        props.dragGuides = dragSnappedSelector(state)
        props.boardFull = boardFullSelector(state)
      }
      return props
    },
    dispatch => ({
      updateViewDate: boardId => dispatch(updateViewDate(boardId)),
      setPlacementPosition: position =>
        dispatch(setPlacementPosition(position)),
      select: (blocks, multi) => dispatch(selectBlocks(blocks, multi)),
      placeBlock: (block, placement) =>
        dispatch(placeBlockAction(block, placement))
    }),
    (stateProps, dispatchProps, ownProps) => ({
      ...stateProps,
      ...dispatchProps,
      ...ownProps,
      placeBlock: (block, placement) =>
        dispatchProps.placeBlock(block, placement)
    })
  ),
  firebaseConnect(({ mode, board }) =>
    mode === 'edit'
      ? [
          `/users/${board.owner.id}/blockCount`,
          `/users/${board.owner.id}/blockLimit`
        ]
      : []
  ),
  withTranslation(),
  trackMouse,
  multiSelect,
  placement,
  dispatchDragHandlers, // Catch drag events and direct to resizeSection, placement or multiSelect
  injectPlacementBlock,
  manageScrollPosition,
  handleDataTransfer
  // withHandlers({
  //   shortcutsHandler: props => action => {
  //     if (props.editMode && action === 'PASTE_BLOCKS')
  //       props.pasteBlocks(props.getScrollTop())
  //     if (props.editMode && action === 'PASTE_BLOCKS_IN_PLACE')
  //       props.pasteBlocks(props.getScrollTop(), true)
  //   }
  // })
)

export const BoardWrapper = styled('div')(({ mode }) => ({
  // safari requires absolte position otherise this element will
  // be more than 100% of the hright of it's parent
  position: 'absolute',
  top: 0,
  bottom: mode === 'discuss' ? rowHeight.default : 0, // room for bar
  width: '100%',
  overflow: 'auto'
}))

class BoardContainer extends Component {
  render() {
    const {
      dragStart,
      drag,
      dragStop,
      height,
      storeRef,
      storeScrollRef,
      mouseMove,
      textEditMode,
      shortcutsHandler,
      placeBlock,
      router,
      ...boardProps
    } = this.props
    const { board, mode, placementBlock } = boardProps
    // board doesn't care about the position, just needs to know it exists
    boardProps.placementBlock = !!boardProps.placementBlock

    const scrollToSection = sectionNumber => {
      const section = Array.from(boardProps.sections.values())[sectionNumber]
      if (scrollElement)
        scrollElement.scrollTop = section.top * boardProps.colWidth
    }
    return (
      // board__wrapper scrolls
      <BoardWrapper mode={mode} innerRef={storeScrollRef}>
        <DraggableCore
          onStart={dragStart}
          onDrag={drag}
          onStop={dragStop}
          // don't allow dragging on a Selectable unless placing
          cancel={
            placementBlock
              ? [
                  SectionResize.toString(),
                  Menu.className,
                  TooltipComponent.toString(),
                  '.color-picker'
                ].join(', ')
              : [
                  SectionResize.toString(),
                  Menu.className,
                  TooltipComponent.toString(),
                  Selectable,
                  '.color-picker'
                ].join(', ')
          }
          disabled={mode !== 'edit' || !!textEditMode}
        >
          <div
            ref={storeRef}
            onMouseMove={mouseMove}
            style={{
              backgroundColor: board.backgroundColor || 'white',
              touchAction: 'initial' // override react-dragable hack that breaks android-chrome (https://github.com/mzabriskie/react-draggable/issues/51#issuecomment-103488278)
            }}
          >
            <Shortcuts
              name="BOARD"
              handler={shortcutsHandler}
              targetNodeSelector="body"
              stopPropagation={false}
              className="react-shortcuts"
            >
              <Board {...boardProps} scrollToSection={scrollToSection} />
            </Shortcuts>
          </div>
        </DraggableCore>
      </BoardWrapper>
    )
  }
}
export default enhance(BoardContainer)
